import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type Hls from 'hls.js';
import type { MediaPlayerClass } from 'dashjs';
import { useApp } from '../context';
import {
  mediaBadgeLabels, browserPlatform, browserSupportsHevc, tmdbRef, formatClock, type SubtitleResult, uuid, isSidecarText, loadSubtitleTrack, type PlaybackDecision, type PlexClient, type PlexItem, type PlexMedia, type PlexStream,
} from '../plex';
import { goBack, navigate } from '../router';

function displayTitleOf(i: PlexItem): string {
  return i.type === 'episode' ? `${i.grandparentTitle ?? ''} · S${i.parentIndex} E${i.index}` : i.title;
}
import { hasManualChoice, loadPrefs, markManualChoice, pickStreams, prefsNow, savePrefs, usePrefs, type Prefs } from '../prefs';
import { hasLoudnessChain, useLoudness } from '../loudness';
import { useTraktScrobble } from '../trakt';
import { loadBif, type Bif } from '../bif';
import { togetherApi, useTogether } from '../together';
import { introdbApi, type Segments } from '../api';
import { RatingCard } from '../components/RatingCard';
import { langCode, PlayerClock, PlayerInfoPanel } from '../components/PlayerOsd';
import { useMeta } from '../meta';
import { call, subsApi, type SubResult } from '../api';

/**
 * Web-player.
 * - "Origineel": speelt het bestand direct af als de browser het aankan (Direct Play);
 *   anders laat Plex het remuxen (Direct Stream) of transcoderen, als HLS.
 *   HEVC (4K HDR) gaat via DASH, zodat de browser het beeld zonder transcode krijgt, net als in Plex Web.
 * - Lagere kwaliteit: altijd via Plex-transcode.
 * - Ander audio- of ondertitelspoor: Plex onthoudt de keuze, de stream start opnieuw op dezelfde plek.
 */

type Quality = 'original' | 20000 | 8000 | 4000;
const QUALITIES: { id: Quality; label: string }[] = [
  { id: 'original', label: 'Origineel' },
  { id: 20000, label: '1080p · 20 Mbps' },
  { id: 8000, label: '1080p · 8 Mbps' },
  { id: 4000, label: '720p · 4 Mbps' },
];

type Mode = { kind: 'direct' } | { kind: 'hls'; decision?: PlaybackDecision; dash?: boolean };
type Menu = null | 'audio' | 'subs' | 'quality' | 'volume' | 'subsearch' | 'together' | 'mark';

const HIDE_AFTER_MS = 3500;

export function Player({ ratingKey, params }: { ratingKey: string; params: URLSearchParams }) {
  const { plex, me } = useApp();
  const video = useRef<HTMLVideoElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const dashRef = useRef<MediaPlayerClass | null>(null);
  const sessionRef = useRef<string>('');
  const hideTimer = useRef<number | undefined>(undefined);

  const [item, setItem] = useState<PlexItem | null>(null);
  const [nextEp, setNextEp] = useState<PlexItem | null>(null);
  const mediaIndex = Number(params.get('media') ?? 0);
  const [quality, setQuality] = useState<Quality>('original');
  const [maxQuality, setMaxQuality] = useState<Quality>('original');
  // Samen kijken: code uit de link (?together=ABCD) of zelf gestart
  const [together, setTogether] = useState<string | null>(() => params.get('together'));
  const applyingRemote = useRef(false);
  const [forceHls, setForceHls] = useState(false);
  // Na een mislukte HEVC/DASH-poging deze sessie de gewone HLS-route nemen
  const [hevcOff, setHevcOff] = useState(false);
  const [startAt, setStartAt] = useState(() => Number(params.get('offset') ?? 0) / 1000);
  const [loadToken, setLoadToken] = useState(0);
  const [mode, setMode] = useState<Mode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | undefined>();
  // Diagnose: laatste gebeurtenissen, zichtbaar via de Info-knop (of toets i) en in de console
  const logRef = useRef<string[]>([]);
  const [, setLogTick] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const dbg = useCallback((msg: string) => {
    const t = new Date().toISOString().slice(11, 19);
    logRef.current = [...logRef.current.slice(-39), `${t} ${msg}`];
    setLogTick((n) => n + 1);
    console.info('[tumedia player]', msg);
  }, []);
  const fail = (msg: string, detail?: string) => {
    dbg(`FOUT: ${msg}${detail ? ` — ${detail}` : ''}`);
    setError(msg);
    setErrorDetail(detail);
    setBuffering(false);
    if (detail) console.warn('[tumedia player]', msg, detail);
  };

  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [buffering, setBuffering] = useState(true);
  const [controls, setControls] = useState(true);
  const [menu, setMenu] = useState<Menu>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [nextDismissed, setNextDismissed] = useState(false);
  const [trackUrl, setTrackUrl] = useState<string | null>(null);
  const prefs = usePrefs();
  // Beeld loopt echt (voor de leeftijdskaart)
  const [running, setRunning] = useState(false);
  // Volume-egalisatie: voorkeur van het profiel, of wat je in de player kiest
  const [loudnessPick, setLoudness] = useState<Prefs['loudness'] | null>(null);
  const loudness = loudnessPick ?? prefs.loudness;
  const loudnessRef = useRef(loudness);
  loudnessRef.current = loudness;
  useLoudness(video, loudness);
  const [notice, setNotice] = useState<string | null>(null);
  const sync = useTogether(together, me.profile?.title ?? 'Iemand', ({ playing: p, position }) => {
    const el = video.current;
    if (!el) return;
    applyingRemote.current = true;
    if (Math.abs(el.currentTime - position) > 1.5) el.currentTime = position;
    if (p && el.paused) el.play().catch(() => undefined);
    if (!p && !el.paused) el.pause();
    window.setTimeout(() => { applyingRemote.current = false; }, 900);
  });
  const shareLocal = () => {
    const el = video.current;
    if (el && together && !applyingRemote.current) sync.send(!el.paused, el.currentTime);
  };
  const startTogether = async () => {
    const el = video.current;
    if (!item || !el) return;
    const r = await togetherApi.create(item.ratingKey, displayTitleOf(item), el.currentTime, !el.paused).catch(() => null);
    if (!r) return;
    setTogether(r.code);
    const q = new URLSearchParams(location.search);
    q.set('together', r.code);
    history.replaceState(null, '', `${location.pathname}?${q}`);
  };
  // Voorbeeldplaatjes boven de tijdbalk
  const [bif, setBif] = useState<Bif | null>(null);
  const [preview, setPreview] = useState<{ x: number; t: number } | null>(null);
  const scrubRef = useRef<HTMLDivElement>(null);
  const rangeRef = useRef<HTMLInputElement>(null);
  /** Plaats (px binnen de tijdbalk-rij) bij een fractie van de duur */
  const xAt = (frac: number): number => {
    const a = scrubRef.current?.getBoundingClientRect();
    const b = rangeRef.current?.getBoundingClientRect();
    return a && b ? b.left - a.left + frac * b.width : 0;
  };
  const previewTimer = useRef<number | undefined>(undefined);
  // Ondertitels: vertraging (s) en zoeken via OpenSubtitles
  const [subDelay, setSubDelay] = useState(0);
  // Ondertitels via tumedia (OpenSubtitles + addons), los van Plex; met automatisch synchroniseren
  const [extResults, setExtResults] = useState<SubResult[] | null>(null);
  const [extSub, setExtSub] = useState<SubResult | null>(null);
  const [extFix, setExtFix] = useState<{ offset: number; scale: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const appliedDelay = useRef(0);
  const [subResults, setSubResults] = useState<(PlexStream & { score?: string | number; providerTitle?: string })[] | null>(null);
  const [subBusy, setSubBusy] = useState<string | null>(null);

  const media: PlexMedia | undefined = item?.Media?.[mediaIndex];
  const part = media?.Part?.[0];
  const streams = part?.Stream ?? [];
  const audioStreams = streams.filter((s) => s.streamType === 2);
  const subStreams = streams.filter((s) => s.streamType === 3);
  const duration = (media?.duration ?? item?.duration ?? 0) / 1000;

  // ---------- Metadata en volgende aflevering ----------

  // Metadata laden en (als je bij deze titel niet zelf gekozen hebt) audio en ondertitels
  // volgens je voorkeuren kiezen, vóórdat het afspelen begint.
  useEffect(() => {
    let live = true;
    (async () => {
      let m = await plex.metadata(ratingKey);
      const p = await loadPrefs();
      // Profielbeheer: maximale kwaliteit voor dit profiel
      const pol = await call<{ policy: { maxQuality: Quality } }>('/api/policy').catch(() => null);
      if (pol && pol.policy.maxQuality !== 'original') {
        setMaxQuality(pol.policy.maxQuality);
        setQuality(pol.policy.maxQuality);
      }
      const mediaPart = m.Media?.[mediaIndex]?.Part?.[0];
      if (mediaPart && !hasManualChoice(ratingKey)) {
        const pick = pickStreams(mediaPart.Stream ?? [], p);
        if (pick.audio || pick.sub !== undefined) {
          await plex.selectStreams(mediaPart.id, {
            ...(pick.audio ? { audioStreamID: pick.audio.id } : {}),
            ...(pick.sub !== undefined ? { subtitleStreamID: pick.sub ? pick.sub.id : 0 } : {}),
          }).catch(() => undefined);
          m = await plex.metadata(ratingKey).catch(() => m);
          dbg(`voorkeuren: ${pick.audio ? `audio ${pick.audio.displayTitle ?? pick.audio.languageCode}` : 'audio ongewijzigd'} · ${pick.sub === undefined ? 'ondertitels ongewijzigd' : pick.sub ? `ondertitels ${pick.sub.displayTitle ?? pick.sub.languageCode}` : 'ondertitels uit'}`);
        }
        // Een ander audiospoor dan het standaardspoor kan de browser niet zelf kiezen: via Plex
        const audios = (m.Media?.[mediaIndex]?.Part?.[0]?.Stream ?? []).filter((x) => x.streamType === 2);
        const def = audios.find((x) => x.default) ?? audios[0];
        const sel = audios.find((x) => x.selected);
        if (sel && def && sel.id !== def.id) setForceHls(true);
      }
      if (live) setItem(m);
    })().catch(() => live && setError('Kon dit item niet laden.'));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plex, ratingKey]);

  useEffect(() => {
    if (item?.type !== 'episode') return;
    findNextEpisode(plex, item).then(setNextEp).catch(() => setNextEp(null));
  }, [plex, item]);

  // ---------- Bron laden ----------

  useEffect(() => {
    const el = video.current;
    if (!el || !item || !media || !part) return;
    let cancelled = false;
    setError(null);
    setErrorDetail(undefined);
    setBuffering(true);

    // Elke afspeelpoging krijgt een eigen sessie: zo koppelt Plex voortgang en stoppen aan deze weergave
    const sessionId = uuid();
    sessionRef.current = sessionId;

    // Tekstondertitels (SRT/VTT) tonen we zelf; alleen beeld-ondertitels (PGS e.d.) moeten ingebrand worden,
    // en dat kost een volledige video-transcode.
    const selectedSub = subStreams.find((s) => s.selected);
    const sidecar = isSidecarText(selectedSub);
    const mustBurn = !!selectedSub && !sidecar;
    const direct = quality === 'original' && !forceHls && !mustBurn && canDirectPlay(media, el);
    if (selectedSub) dbg(`ondertitels: ${selectedSub.codec} ${sidecar ? '(zelf tonen)' : '(inbranden door Plex)'}`);

    const begin = () => {
      if (startAt > 0) el.currentTime = startAt;
      el.play().catch(() => setPlaying(false)); // autoplay kan geblokkeerd zijn; dan wacht de play-knop
    };

    // Web Audio (volume-egalisatie) kan alleen bij geluid van een andere server als die met CORS geladen is
    // (en altijd als het geluid al via Web Audio loopt, anders blijft het stil)
    const wantCors = loudnessRef.current !== 'off' || prefsNow().loudness !== 'off' || hasLoudnessChain(el);
    el.crossOrigin = wantCors ? 'anonymous' : null;

    dbg(`bestand: ${media.container}/${media.videoCodec}/${media.audioCodec} ${media.videoResolution ?? ''} · browser ${browserPlatform()} · ${direct ? 'Direct Play' : 'via Plex (HLS)'}${startAt ? ` · start ${Math.round(startAt)}s` : ''}`);
    if (direct) {
      setMode({ kind: 'direct' });
      el.src = plex.directUrl(part);
      el.addEventListener('loadedmetadata', begin, { once: true });
    } else {
      const opts = {
        ratingKey: item.ratingKey,
        mediaIndex,
        sessionId,
        clientId: plex.clientId,
        maxBitrateKbps: quality === 'original' ? undefined : quality,
        subtitles: mustBurn ? ('burn' as const) : ('none' as const),
      };
      setMode({ kind: 'hls' });

      // HEVC-bestand en een browser die HEVC kan decoderen: probeer Direct Stream via DASH (zoals Plex Web).
      // Dolby Vision profiel 5 heeft geen HDR10-basislaag (paarse/groene kleuren in de browser), dus die niet.
      const vStream = streams.find((s) => s.streamType === 1);
      const tryHevc =
        quality === 'original' && !mustBurn && !hevcOff &&
        (media.videoCodec ?? '').toLowerCase() === 'hevc' && vStream?.DOVIProfile !== 5 && browserSupportsHevc();
      if (media.videoCodec?.toLowerCase() === 'hevc') dbg(`HEVC in deze browser: ${browserSupportsHevc() ? 'ja' : 'nee'}${hevcOff ? ' (DASH uitgezet na fout)' : ''}`);

      const startDash = async (): Promise<boolean> => {
        const dOpts = { ...opts, protocol: 'dash' as const, hevc: true };
        let decision: PlaybackDecision;
        try {
          decision = await plex.decision(dOpts);
        } catch (err) {
          dbg(`DASH-beslissing mislukt: ${String(err)}`);
          return false;
        }
        if (cancelled) return true;
        dbg(`Plex-beslissing (DASH/HEVC): code ${decision.code ?? '?'} · video ${decision.videoCodec ?? '?'} ${decision.videoDecision} · audio ${decision.audioDecision}${decision.reason ? ` · ${decision.reason}` : ''}`);
        if ((decision.code !== undefined && decision.code >= 2000) || decision.videoDecision !== 'copy') return false;

        const mod = await import('dashjs');
        const dashjs = ((mod as unknown as { default?: typeof mod.default }).default ?? mod) as typeof mod.default;
        if (cancelled) return true;
        setMode({ kind: 'hls', decision, dash: true });
        const src = plex.dashUrl(dOpts);
        dbg(`stream: ${new URL(src).host} · DASH (dash.js)`);
        const player = dashjs.MediaPlayer().create();
        dashRef.current = player;
        player.on(dashjs.MediaPlayer.events.ERROR, (e: unknown) => {
          const err = (e as { error?: { code?: number; message?: string } }).error;
          dbg(`DASH-fout: ${err?.code ?? '?'} ${err?.message ?? ''}`);
          if (cancelled) return;
          // Terugvallen op de gewone route (Plex transcodeert dan zo nodig), op dezelfde plek
          if (el.currentTime > 1) setStartAt(el.currentTime);
          setHevcOff(true);
        });
        el.addEventListener('loadedmetadata', begin, { once: true });
        player.initialize(el, src, false);
        return true;
      };

      const startHls = async () => {
        if (tryHevc && (await startDash())) return;
        if (cancelled) return;
        // Eerst de beslissing ophalen: Plex start de sessie pas goed als die er is,
        // en zo zien we meteen of Plex dit bestand voor deze browser kan leveren.
        let decision: PlaybackDecision | undefined;
        try {
          decision = await plex.decision(opts);
        } catch (err) {
          if (!cancelled) fail('Plex kon het afspelen niet voorbereiden.', String(err));
          return;
        }
        if (cancelled) return;
        dbg(`Plex-beslissing: code ${decision.code ?? '?'} · video ${decision.videoCodec ?? '?'} ${decision.videoDecision} · audio ${decision.audioDecision}${decision.reason ? ` · ${decision.reason}` : ''}`);
        setMode({ kind: 'hls', decision });
        if (decision.code !== undefined && decision.code >= 2000) {
          fail('Plex kan dit bestand niet afspelen in deze browser.', decision.reason);
          return;
        }

        const src = plex.hlsUrl(opts);
        // Met volume-egalisatie liever hls.js (Media Source): dan kan Web Audio altijd bij het geluid
        const nativeHls = el.canPlayType('application/vnd.apple.mpegurl') !== '' && !(loudnessRef.current !== 'off' && 'MediaSource' in window);
        dbg(`stream: ${new URL(src).host} · ${nativeHls ? 'native HLS' : 'hls.js'}`);
        if (nativeHls) {
          el.src = src;
          el.addEventListener('loadedmetadata', begin, { once: true });
          return;
        }

        const { default: HlsLib } = await import('hls.js');
        if (cancelled) return;
        if (!HlsLib.isSupported()) {
          fail('Deze browser kan deze video niet afspelen.');
          return;
        }
        const hls = new HlsLib({ startPosition: startAt > 0 ? startAt : -1, maxBufferLength: 60, backBufferLength: 30 });
        hlsRef.current = hls;
        let recoveries = 0;
        let frags = 0;
        hls.on(HlsLib.Events.MANIFEST_LOADED, (_e, d) => dbg(`manifest geladen (${d.levels.length} niveau(s))`));
        hls.on(HlsLib.Events.LEVEL_LOADED, (_e, d) => dbg(`playlist: ${d.details.fragments.length} segmenten, ${Math.round(d.details.totalduration)}s${d.details.live ? ' (live/groeiend)' : ''}`));
        hls.on(HlsLib.Events.FRAG_LOADED, (_e, d) => {
          frags += 1;
          if (frags <= 3) dbg(`segment ${d.frag.sn} geladen (${Math.round(d.frag.start)}s)`);
        });
        hls.on(HlsLib.Events.BUFFER_CODECS, (_e, d) => dbg(`codecs: ${Object.values(d).map((t) => (t as { codec?: string; container?: string })?.codec ?? (t as { container?: string })?.container).filter(Boolean).join(', ')}`));
        hls.on(HlsLib.Events.ERROR, (_e, data) => {
          dbg(`hls ${data.fatal ? 'FATALE ' : ''}fout: ${data.type} / ${data.details}${data.response?.code ? ` (HTTP ${data.response.code})` : ''}`);
          if (!data.fatal) return;
          const detail = `${data.type} / ${data.details}${data.response?.code ? ` (HTTP ${data.response.code})` : ''}`;
          // Mediafouten één keer proberen te herstellen; daarna niet eindeloos stil blijven proberen
          if (data.type === HlsLib.ErrorTypes.MEDIA_ERROR && recoveries < 2) {
            recoveries += 1;
            if (recoveries === 1) hls.recoverMediaError();
            else {
              hls.swapAudioCodec();
              hls.recoverMediaError();
            }
            return;
          }
          fail(
            data.type === HlsLib.ErrorTypes.MEDIA_ERROR
              ? 'De browser kan dit beeld- of geluidsformaat niet decoderen.'
              : 'De stream van Plex kwam niet door.',
            `${detail}${decision?.videoCodec ? ` · video: ${decision.videoCodec} (${decision.videoDecision})` : ''}`,
          );
        });
        hls.on(HlsLib.Events.MANIFEST_PARSED, () => {
          el.play().catch(() => setPlaying(false));
        });
        hls.loadSource(src);
        hls.attachMedia(el);
      };

      startHls().catch((err) => fail('Player laden mislukt.', String(err)));
    }

    return () => {
      cancelled = true;
      el.removeEventListener('loadedmetadata', begin);
      // Eerst Plex laten weten dat deze sessie stopt (met dezelfde sessie-ID), dan pas opruimen
      void plex.timeline(item, 'stopped', el.currentTime * 1000, sessionId, true).catch(() => undefined);
      if (el.duration > 0) scrobbleRef.current('stop', (el.currentTime / el.duration) * 100);
      if (!direct) plex.stopTranscode(sessionId);
      hlsRef.current?.destroy();
      hlsRef.current = null;
      dashRef.current?.reset();
      dashRef.current = null;
      el.removeAttribute('src');
      el.load();
      if (sessionRef.current === sessionId) sessionRef.current = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bewust alleen herladen bij deze wijzigingen
  }, [plex, item?.ratingKey, mediaIndex, quality, forceHls, hevcOff, loadToken]);

  // ---------- Waakhond: afspelen komt niet op gang ----------

  useEffect(() => {
    if (error) return;
    let last = -1;
    let stuckFor = 0;
    const t = window.setInterval(() => {
      const el = video.current;
      if (!el || el.paused) {
        stuckFor = 0;
        return;
      }
      if (Math.abs(el.currentTime - last) < 0.05) stuckFor += 2;
      else stuckFor = 0;
      last = el.currentTime;
      if (stuckFor === 10 || stuckFor === 20) dbg(`staat stil: readyState ${el.readyState}, networkState ${el.networkState}, tijd ${el.currentTime.toFixed(1)}s, buffer ${bufferedText(el)}`);
      if (stuckFor >= 30) {
        window.clearInterval(t);
        fail('Het afspelen komt niet op gang.', `readyState ${el.readyState} · networkState ${el.networkState} · buffer ${bufferedText(el)} · beeld ${el.videoWidth}x${el.videoHeight}`);
        setShowInfo(true);
      }
    }, 2000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, loadToken, quality, forceHls]);

  // ---------- Voortgang naar Plex ----------

  const scrobble = useTraktScrobble(plex, item);
  const scrobbleRef = useRef(scrobble);
  scrobbleRef.current = scrobble;
  const report = useCallback((state: 'playing' | 'paused' | 'stopped') => {
    const el = video.current;
    if (!item || !el) return;
    if (!sessionRef.current) return;
    void plex.timeline(item, state, el.currentTime * 1000, sessionRef.current).catch(() => undefined);
    const total = el.duration || duration;
    if (total > 0) scrobble(state === 'playing' ? 'start' : state === 'paused' ? 'pause' : 'stop', (el.currentTime / total) * 100);
  }, [plex, item, scrobble, duration]);

  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => report('playing'), 10_000);
    return () => window.clearInterval(t);
  }, [playing, report]);

  // Tabblad sluiten of wegnavigeren: ook dan 'stopped' melden
  useEffect(() => {
    const onHide = () => {
      const el = video.current;
      if (item && el && sessionRef.current) {
        void plex.timeline(item, 'stopped', el.currentTime * 1000, sessionRef.current, true).catch(() => undefined);
        if (mode?.kind === 'hls') plex.stopTranscode(sessionRef.current);
      }
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [plex, item, mode]);

  // Tekstondertitels laden als <track>
  useEffect(() => {
    const sub = subStreams.find((s) => s.selected);
    if (!isSidecarText(sub)) {
      setTrackUrl(null);
      return;
    }
    let url: string | null = null;
    loadSubtitleTrack(plex, sub!)
      .then((u) => {
        url = u;
        setTrackUrl(u);
      })
      .catch((err) => dbg(`ondertitels laden mislukt: ${String(err)}`));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plex, subStreams.find((s) => s.selected)?.id]);

  // ---------- Bediening tonen/verbergen ----------

  const poke = useCallback(() => {
    setControls(true);
    window.clearTimeout(hideTimer.current);
    hideTimer.current = window.setTimeout(() => {
      if (!video.current?.paused) {
        setControls(false);
        setMenu(null);
      }
    }, HIDE_AFTER_MS);
  }, []);

  useEffect(() => {
    poke();
    return () => window.clearTimeout(hideTimer.current);
  }, [poke]);

  const togglePlay = useCallback(() => {
    const el = video.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }, []);

  const seekBy = useCallback((sec: number) => {
    const el = video.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min((duration || el.duration) - 1, el.currentTime + sec));
    // Even het plaatje laten zien waar je naartoe spoelt
    if (bif && duration) {
      setPreview({ x: xAt(el.currentTime / duration), t: el.currentTime });
      window.clearTimeout(previewTimer.current);
      previewTimer.current = window.setTimeout(() => setPreview(null), 1200);
    }
  }, [duration, bif]);

  // BIF laden zodra we weten welk bestand speelt
  useEffect(() => {
    setBif(null);
    const url = plex.bifUrl(part);
    if (!url) return;
    let live = true;
    void loadBif(url).then((b) => live && setBif(b));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plex, part?.id]);

  // Ondertitelvertraging: de cues van het (zelf getoonde) spoor verschuiven
  useEffect(() => {
    const track = video.current?.textTracks?.[0];
    const delta = subDelay - appliedDelay.current;
    if (!track?.cues || !delta) return;
    for (let i = 0; i < track.cues.length; i++) {
      const c = track.cues[i];
      c.startTime = Math.max(0, c.startTime + delta);
      c.endTime = Math.max(0, c.endTime + delta);
    }
    appliedDelay.current = subDelay;
  }, [subDelay]);
  useEffect(() => {
    // Nieuw ondertitelspoor: vertraging weer op 0
    appliedDelay.current = 0;
    setSubDelay(0);
  }, [trackUrl]);

  const searchSubs = async () => {
    if (!item) return;
    setMenu('subsearch');
    setSubResults(null);
    const lang = (prefs.subLangs[0] ?? 'nld');
    const two: Record<string, string> = { nld: 'nl', eng: 'en', deu: 'de', fra: 'fr', spa: 'es', ita: 'it', por: 'pt', jpn: 'ja', kor: 'ko', zho: 'zh', tur: 'tr', ara: 'ar' };
    setExtResults(null);
    const imdb = item.Guid?.find((g) => g.id.startsWith('imdb://'))?.id.slice(7);
    const tmdb = item.type === 'movie' ? Number(item.Guid?.find((g) => g.id.startsWith('tmdb://'))?.id.slice(7)) || undefined : undefined;
    const release = (part?.file ?? '').split(/[\\/]/).pop() ?? '';
    // Afleveringen: OpenSubtitles kent ook het IMDb-nummer van de aflevering zelf
    const ours = imdb || tmdb
      ? subsApi.search({ id: imdb ?? '', tmdb, type: 'movie', release, langs: prefs.subLangs.length ? prefs.subLangs : ['nld'] }).then((x) => x.results).catch(() => [] as SubResult[])
      : Promise.resolve([] as SubResult[]);
    const [r, mine] = await Promise.all([plex.searchSubtitles(item.ratingKey, two[lang] ?? 'nl').catch(() => [] as SubtitleResult[]), ours]);
    setSubResults(r.sort((a, b) => Number(b.score ?? 0) - Number(a.score ?? 0)).slice(0, 12));
    setExtResults(mine.slice(0, 12));
  };

  /** Ondertitel van tumedia kiezen: Plex-ondertitel uit, de bewaarde correctie (sync) erbij. */
  const chooseExtSub = async (r: SubResult | null) => {
    setMenu(null);
    if (r && subStreams.some((x) => x.selected)) await chooseSub(null);
    setExtSub(r);
    let fix: { offset: number; scale: number } | null = null;
    try { fix = r ? JSON.parse(localStorage.getItem(`subsync:${r.fileId}`) ?? 'null') : null; } catch { /* geen opslag */ }
    setExtFix(fix);
    // Slechte match en een betere referentie beschikbaar: meteen synchroniseren
    if (r && !fix && r.match < 60 && (extResults ?? []).some((x) => x.fileId !== r.fileId && x.match >= 60)) void autoSync(r);
  };

  const autoSync = async (r: SubResult | null = extSub) => {
    const ref = (extResults ?? []).filter((x) => r && x.fileId !== r.fileId).sort((a, b) => b.match - a.match)[0];
    if (!r || !ref) { setNotice('Geen tweede ondertitel om mee te vergelijken'); return; }
    setSyncing(true);
    try {
      const x = await call<{ offset: number; scale: number; score: number; ok: boolean }>(`/api/subs/sync/${r.fileId}?ref=${ref.fileId}`);
      if (x.ok) {
        const fix = { offset: x.offset, scale: x.scale };
        setExtFix(fix);
        try { localStorage.setItem(`subsync:${r.fileId}`, JSON.stringify(fix)); } catch { /* geen opslag */ }
        setNotice(`Ondertitels gesynchroniseerd (${x.offset > 0 ? '+' : ''}${x.offset.toFixed(1)} s)`);
      } else setNotice('Synchroniseren lukte niet zeker genoeg');
    } catch {
      setNotice('Synchroniseren mislukt');
    } finally {
      setSyncing(false);
    }
  };

  const downloadSub = async (s: PlexStream & { key?: string }) => {
    if (!item || !part || !s.key) return;
    setSubBusy(s.displayTitle ?? 'ondertitel');
    const before = new Set((part.Stream ?? []).filter((x) => x.streamType === 3).map((x) => x.id));
    await plex.downloadSubtitle(item.ratingKey, s.key).catch(() => undefined);
    // Plex downloadt op de achtergrond: even wachten tot het nieuwe spoor er is
    for (let i = 0; i < 12; i++) {
      await new Promise((r) => setTimeout(r, 1500));
      const fresh = await plex.metadata(item.ratingKey).catch(() => null);
      const subs = fresh?.Media?.[mediaIndex]?.Part?.[0]?.Stream?.filter((x) => x.streamType === 3) ?? [];
      const added = subs.find((x) => !before.has(x.id));
      if (fresh && added) {
        setSubBusy(null);
        setMenu(null);
        setItem(fresh);
        await chooseSub(added);
        setNotice('Ondertitels toegevoegd');
        return;
      }
    }
    setSubBusy(null);
    setNotice('Downloaden duurt lang; probeer het zo opnieuw');
  };

  const exit = useCallback(() => {
    if (document.fullscreenElement) void document.exitFullscreen().catch(() => undefined);
    goBack();
  }, []);

  // Telefoon als afstandsbediening: spoelen
  useEffect(() => {
    const onSeek = (e: Event) => {
      seekBy(Number((e as CustomEvent<number>).detail) || 0);
      poke();
    };
    window.addEventListener('tumedia:seek', onSeek);
    return () => window.removeEventListener('tumedia:seek', onSeek);
  });

  // Toetsen: vóór de globale navigatie (capture), zodat pijltjes zonder zichtbare bediening spoelen.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key;
      if (k === 'Escape' || (k === 'Backspace' && (e.target as HTMLElement)?.tagName !== 'INPUT')) {
        e.preventDefault();
        e.stopPropagation();
        if (menu) setMenu(null);
        else if (controls && !video.current?.paused) setControls(false);
        else exit();
        return;
      }
      if (k === 'i') {
        setShowInfo((v) => !v);
        return;
      }
      if (k === ' ' || k === 'k' || k === 'MediaPlayPause') {
        if ((e.target as HTMLElement)?.tagName === 'BUTTON' && k === ' ') return; // spatie op een knop = die knop
        e.preventDefault();
        togglePlay();
        poke();
        return;
      }
      if (!controls && (k === 'ArrowLeft' || k === 'ArrowRight')) {
        e.preventDefault();
        e.stopPropagation();
        seekBy(k === 'ArrowLeft' ? -10 : 30);
        poke();
        return;
      }
      if (!controls && (k === 'ArrowUp' || k === 'ArrowDown' || k === 'Enter')) {
        e.preventDefault();
        e.stopPropagation();
        poke();
        return;
      }
      poke();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [controls, menu, exit, poke, seekBy, togglePlay]);

  // ---------- Intro overslaan en volgende aflevering ----------

  // IntroDB vult aan wat Plex zelf niet heeft (en levert ook de samenvatting "previously on")
  const [segs, setSegs] = useState<Segments | null>(null);
  const [introdbKey, setIntrodbKey] = useState(false);
  useEffect(() => {
    if (!item || (item.type !== 'episode' && item.type !== 'movie')) return;
    let live = true;
    introdbApi.segments(item.ratingKey).then((s) => live && setSegs(s)).catch(() => undefined);
    introdbApi.status().then((r) => live && setIntrodbKey(r.configured)).catch(() => undefined);
    return () => {
      live = false;
    };
  }, [item?.ratingKey, item?.type]);

  const plexIntro = item?.Marker?.find((m) => m.type === 'intro');
  const plexCredits = item?.Marker?.find((m) => m.type === 'credits');
  const intro = plexIntro ?? (segs?.intro ? { type: 'intro', startTimeOffset: segs.intro.start, endTimeOffset: segs.intro.end } : undefined);
  const credits = plexCredits ?? (segs?.outro ? { type: 'credits', startTimeOffset: segs.outro.start, endTimeOffset: segs.outro.end } : undefined);
  const recap = segs?.recap ?? null;
  const inIntro = !!intro && time * 1000 >= intro.startTimeOffset && time * 1000 < intro.endTimeOffset - 1000;
  const inRecap = !!recap && time * 1000 >= recap.start && time * 1000 < recap.end - 1000;
  const nextStartsAt = credits ? credits.startTimeOffset / 1000 : duration - 30;
  const showNext = !!nextEp && !nextDismissed && duration > 0 && time >= nextStartsAt;

  // Intro automatisch overslaan (één keer per intro; terugspoelen mag)
  const skippedIntro = useRef<number | null>(null);
  useEffect(() => {
    if (!prefs.autoSkipIntro || !inIntro || !intro || !video.current) return;
    if (skippedIntro.current === intro.startTimeOffset) return;
    skippedIntro.current = intro.startTimeOffset;
    video.current.currentTime = intro.endTimeOffset / 1000;
    setNotice('Intro overgeslagen');
  }, [prefs.autoSkipIntro, inIntro, intro]);

  // Samenvatting ("previously on") ook automatisch overslaan als intro's dat doen
  const skippedRecap = useRef<number | null>(null);
  useEffect(() => {
    if (!prefs.autoSkipIntro || !inRecap || !recap || !video.current) return;
    if (skippedRecap.current === recap.start) return;
    skippedRecap.current = recap.start;
    video.current.currentTime = recap.end / 1000;
    setNotice('Samenvatting overgeslagen');
  }, [prefs.autoSkipIntro, inRecap, recap]);

  // Zelf tijden markeren en naar IntroDB sturen (met je eigen sleutel)
  const [mark, setMark] = useState<{ type: 'intro' | 'recap' | 'outro'; start: number | null; end: number | null; msg: string | null }>({ type: 'intro', start: null, end: null, msg: null });
  const sendMark = async () => {
    if (!item || mark.start === null || mark.end === null) return;
    setMark((m) => ({ ...m, msg: 'Versturen…' }));
    try {
      await introdbApi.submit(item.ratingKey, mark.type, mark.start, mark.end);
      setMark({ type: mark.type, start: null, end: null, msg: '✓ Bedankt! Ingestuurd bij IntroDB.' });
    } catch (e) {
      setMark((m) => ({ ...m, msg: e instanceof Error && e.message ? e.message : 'Insturen mislukt.' }));
    }
  };

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 2500);
    return () => window.clearTimeout(t);
  }, [notice]);

  const playNext = useCallback(() => {
    if (!nextEp) return;
    report('stopped');
    navigate(`/play/${nextEp.ratingKey}`, { replace: true });
  }, [nextEp, report]);

  useEffect(() => {
    if (!showNext) {
      setCountdown(null);
      return;
    }
    if (!prefs.autoplayNext) return; // alleen de knoppen tonen
    setCountdown((c) => c ?? prefs.nextCountdown);
    const t = window.setInterval(() => setCountdown((c) => (c === null ? null : c - 1)), 1000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNext]);

  useEffect(() => {
    if (countdown !== null && countdown <= 0) playNext();
  }, [countdown, playNext]);

  // ---------- Sporen en kwaliteit ----------

  const reloadHere = () => {
    setStartAt(video.current?.currentTime ?? 0);
    setLoadToken((n) => n + 1);
  };

  const chooseAudio = async (s: PlexStream) => {
    if (!part || !item) return;
    setMenu(null);
    markManualChoice(item.ratingKey);
    await plex.selectStreams(part.id, { audioStreamID: s.id }).catch(() => undefined);
    const fresh = await plex.metadata(item.ratingKey).catch(() => null);
    if (fresh) setItem(fresh);
    setForceHls(true); // een ander audiospoor kan alleen via Plex
    reloadHere();
  };

  const chooseSub = async (s: PlexStream | null) => {
    if (!part || !item) return;
    setMenu(null);
    if (s) setExtSub(null);
    markManualChoice(item.ratingKey);
    await plex.selectStreams(part.id, { subtitleStreamID: s ? s.id : 0 }).catch(() => undefined);
    const fresh = await plex.metadata(item.ratingKey).catch(() => null);
    if (fresh) setItem(fresh);
    reloadHere();
  };

  const chooseLoudness = (v: Prefs['loudness']) => {
    setMenu(null);
    const before = loudnessRef.current;
    setLoudness(v);
    void savePrefs({ loudness: v }).catch(() => undefined);
    // Direct afspelen of native HLS: opnieuw laden zodat het geluid via Web Audio kan (CORS-modus)
    if (before === 'off' && v !== 'off' && !hlsRef.current && !dashRef.current) reloadHere();
  };

  const chooseQuality = (q: Quality) => {
    setMenu(null);
    setStartAt(video.current?.currentTime ?? 0);
    setQuality(q);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void root.current?.requestFullscreen().catch(() => undefined);
  };

  const modeLabel = useMemo(() => {
    if (!mode) return '';
    if (mode.kind === 'direct') return 'Direct Play';
    const d = mode.decision;
    if (!d) return 'Plex-stream';
    if (d.videoDecision === 'copy') {
      const label = d.audioDecision === 'copy' ? 'Direct Stream' : 'Direct Stream · audio omgezet';
      return mode.dash ? `${label} · ${(d.videoCodec ?? 'hevc').toUpperCase()}` : label;
    }
    return 'Transcode';
  }, [mode]);

  // ---------- Weergave ----------

  // Info in de speler: logo, scores en wanneer de serie terugkomt (van de serie bij een aflevering)
  const [osdRef, setOsdRef] = useState<{ type: 'movie' | 'tv'; tmdbId: number } | null>(null);
  useEffect(() => {
    if (!item) return;
    if (item.type !== 'episode') { setOsdRef(tmdbRef(item)); return; }
    if (item.grandparentRatingKey) plex.metadata(item.grandparentRatingKey).then((s) => setOsdRef(tmdbRef(s))).catch(() => setOsdRef(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.ratingKey]);
  const osdMeta = useMeta(osdRef);
  const osdFlags = useMemo(() => (item ? mediaBadgeLabels(item, item.Media?.[0]) : []), [item]);
  const title = item?.type === 'episode' ? item.grandparentTitle : item?.title;
  const sub = item?.type === 'episode' ? `S${item.parentIndex} E${item.index} · ${item.title}` : item?.year ? String(item.year) : '';

  return (
    <div
      ref={root}
      className={`player ${controls ? 'show-controls' : ''}`}
      data-sub-size={prefs.subStyle.size}
      data-sub-color={prefs.subStyle.color}
      data-sub-bg={prefs.subStyle.background}
      onMouseMove={poke}
      onClick={(e) => {
        if (e.target === video.current) {
          if (controls) togglePlay();
          poke();
        }
      }}
    >
      {item && (
        <RatingCard
          show={running}
          imdb={item.Guid?.find((g) => g.id.startsWith('imdb://'))?.id.slice(7) ?? null}
          tmdb={item.type === 'movie' ? Number(item.Guid?.find((g) => g.id.startsWith('tmdb://'))?.id.slice(7)) || null : null}
          type={item.type === 'movie' ? 'movie' : 'tv'}
          age={item.contentRating ?? null}
          tech={techLine(item)}
        />
      )}
      <video
        ref={video}
        className="player-video"
        playsInline
        onPlay={() => { setPlaying(true); report('playing'); shareLocal(); }}
        onPause={() => { setPlaying(false); report('paused'); setControls(true); shareLocal(); }}
        onSeeked={shareLocal}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => dbg(`metadata: ${e.currentTarget.videoWidth}x${e.currentTarget.videoHeight}, ${Math.round(e.currentTarget.duration)}s`)}
        onWaiting={() => { setBuffering(true); dbg('wacht op data'); }}
        onStalled={() => dbg('netwerk stokt (stalled)')}
        onPlaying={() => { setBuffering(false); setRunning(true); dbg('speelt'); }}
        onCanPlay={() => setBuffering(false)}
        onEnded={() => { report('stopped'); if (nextEp) playNext(); else setControls(true); }}
        onError={() => {
          if (mode?.kind === 'direct') {
            // Browser kon het bestand toch niet aan: via Plex opnieuw proberen
            setForceHls(true);
            reloadHere();
          } else if (!hlsRef.current) {
            const e = video.current?.error;
            fail('Afspelen mislukt.', e ? `MediaError ${e.code}${e.message ? `: ${e.message}` : ''}` : undefined);
          }
        }}
      >
        {(extSub || trackUrl) && (
          <track
            key={`${extSub ? `ext${extSub.fileId}:${extFix?.offset ?? 0}:${extFix?.scale ?? 1}` : trackUrl}:${prefs.subStyle.position}`}
            kind="subtitles"
            src={extSub ? subsApi.fileUrl(extSub.fileId) : trackUrl!}
            default
            label="Ondertitels"
            onLoad={(e) => {
              // Gesynchroniseerd: tijden corrigeren (verschuiving en snelheid)
              const all = e.currentTarget.track.cues;
              if (extSub && extFix && all) {
                for (let i = 0; i < all.length; i++) {
                  const c = all[i];
                  c.startTime = Math.max(0, c.startTime * extFix.scale + extFix.offset);
                  c.endTime = Math.max(0, c.endTime * extFix.scale + extFix.offset);
                }
              }
              // Hoger zetten, bijv. boven ingebrande tekst of de tv-rand
              const cues = e.currentTarget.track.cues;
              if (!cues || prefs.subStyle.position !== 'raised') return;
              for (let i = 0; i < cues.length; i++) {
                const c = cues[i] as VTTCue;
                c.snapToLines = true;
                c.line = -4;
              }
            }}
          />
        )}
      </video>

      {buffering && !error && <div className="player-spinner" aria-label="Laden" role="status" />}
      {item && !error && !buffering && <PauseInfo item={item} paused={!playing} />}

      {error && (
        <div className="player-error" role="alert">
          <p>{error}</p>
          {errorDetail && <p className="subtle" style={{ fontSize: 'calc(var(--u) * 1.15)', maxWidth: '60ch' }}>{errorDetail}</p>}
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" data-autofocus onClick={reloadHere}>Opnieuw proberen</button>
            {quality === 'original' && <button className="btn" onClick={() => chooseQuality(8000)}>Lagere kwaliteit</button>}
            <button className="btn" onClick={exit}>Terug</button>
          </div>
        </div>
      )}

      {showInfo && (
        <div className="player-info" aria-label="Diagnose">
          <div className="player-info-head">
            <strong>Diagnose</strong>
            <button
              className="chip"
              onClick={() => void navigator.clipboard?.writeText(logRef.current.join('\n')).catch(() => undefined)}
            >
              Kopiëren
            </button>
          </div>
          <pre>{logRef.current.join('\n') || 'Nog geen gebeurtenissen'}</pre>
        </div>
      )}

      {notice && <div className="player-notice" role="status">{notice}</div>}

      {inRecap && !prefs.autoSkipIntro && !inIntro && (
        <button className="btn btn-primary player-skip" data-autofocus onClick={() => { if (video.current && recap) video.current.currentTime = recap.end / 1000; }}>
          Samenvatting overslaan
        </button>
      )}

      {inIntro && !prefs.autoSkipIntro && (
        <button className="btn btn-primary player-skip" data-autofocus onClick={() => { if (video.current && intro) video.current.currentTime = intro.endTimeOffset / 1000; }}>
          Intro overslaan
        </button>
      )}

      {showNext && nextEp && (
        <div className="player-next" role="dialog" aria-label="Volgende aflevering">
          <span className="subtle">Volgende aflevering{countdown !== null ? ` over ${Math.max(0, countdown)}s` : ''}</span>
          <strong>S{nextEp.parentIndex} E{nextEp.index} · {nextEp.title}</strong>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn btn-primary" data-autofocus onClick={playNext}>Nu afspelen</button>
            <button className="btn" onClick={() => setNextDismissed(true)}>Credits kijken</button>
          </div>
        </div>
      )}

      <div className="player-top" aria-hidden={!controls}>
        <button className="icon-btn" onClick={exit} aria-label="Terug">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
        <div className="player-title">
          <strong>{title}</strong>
          {sub && <span className="muted">{sub}</span>}
        </div>
        {modeLabel && (
          <span className={`mode-badge ${mode?.kind === 'hls' && mode.decision?.videoDecision === 'transcode' ? 'is-transcode' : ''}`} title={mode?.kind === 'hls' ? mode.decision?.reason : undefined}>
            {modeLabel}
          </span>
        )}
        <PlayerClock remaining={duration > 0 ? duration - time : null} />
      </div>

      <div className="player-bottom" aria-hidden={!controls}>
        {item && (
          <PlayerInfoPanel
            info={{ title: title ?? item.title, line: item.type === 'episode' ? `S${item.parentIndex} · A${item.index} · ${item.title}` : item.year ? String(item.year) : null, flags: osdFlags }}
            meta={osdMeta}
          />
        )}
        <div className="scrub" ref={scrubRef}>
          <span className="time">{formatClock(time)}</span>
          {preview && bif && (() => {
            const src = bif.frameAt(preview.t);
            return src ? (
              <div className="scrub-preview" style={{ left: `${Math.round(preview.x)}px` }} aria-hidden="true">
                <img src={src} alt="" />
                <span>{formatClock(preview.t)}</span>
              </div>
            ) : null;
          })()}
          <input
            onPointerMove={(e) => {
              if (!bif || !duration) return;
              const r = e.currentTarget.getBoundingClientRect();
              const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
              setPreview({ x: xAt(frac), t: frac * duration });
            }}
            onPointerLeave={() => setPreview(null)}
            ref={rangeRef}
            type="range"
            className="scrub-range"
            aria-label="Positie"
            min={0}
            max={Math.max(1, Math.floor(duration))}
            step={1}
            value={Math.floor(time)}
            onChange={(e) => {
              if (video.current) video.current.currentTime = Number(e.target.value);
              if (bif && duration) {
                setPreview({ x: xAt(Number(e.target.value) / duration), t: Number(e.target.value) });
                window.clearTimeout(previewTimer.current);
                previewTimer.current = window.setTimeout(() => setPreview(null), 1200);
              }
              poke();
            }}
          />
          <span className="time">-{formatClock(Math.max(0, duration - time))}</span>
        </div>

        <div className="player-buttons" data-focus-group>
          <button className="icon-btn" onClick={() => seekBy(-10)} aria-label="10 seconden terug">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /></svg>
          </button>
          <button className="icon-btn icon-btn-lg" onClick={togglePlay} aria-label={playing ? 'Pauzeren' : 'Afspelen'} data-autofocus>
            {playing ? (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" /></svg>
            ) : (
              <svg width="30" height="30" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 4l13 8-13 8z" /></svg>
            )}
          </button>
          <button className="icon-btn" onClick={() => seekBy(30)} aria-label="30 seconden vooruit">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></svg>
          </button>

          <div className="spacer" />

          {audioStreams.length > 1 && (
            <button className="chip chip-lang" aria-expanded={menu === 'audio'} onClick={() => setMenu(menu === 'audio' ? null : 'audio')}>
              Audio{langCode(audioStreams.find((s) => s.selected)?.languageCode) && <small>{langCode(audioStreams.find((s) => s.selected)?.languageCode)}</small>}
            </button>
          )}
          <button className="chip chip-lang" aria-expanded={menu === 'subs' || menu === 'subsearch'} onClick={() => setMenu(menu === 'subs' || menu === 'subsearch' ? null : 'subs')}>
            Ondertitels<small>{langCode(subStreams.find((s) => s.selected)?.languageCode) ?? 'UIT'}</small>
          </button>
          <button className="chip" aria-expanded={menu === 'volume'} aria-label={`Volume-egalisatie: ${loudness === 'off' ? 'uit' : loudness === 'night' ? 'nachtmodus' : 'aan'}`} onClick={() => setMenu(menu === 'volume' ? null : 'volume')}>
            {loudness === 'night' ? 'Nachtmodus' : loudness === 'normal' ? 'Volume: gelijk' : 'Volume'}
          </button>
          {introdbKey && (
            <button className="chip" aria-expanded={menu === 'mark'} onClick={() => setMenu(menu === 'mark' ? null : 'mark')}>Markeren</button>
          )}
          <button className="chip" aria-expanded={menu === 'together'} onClick={() => setMenu(menu === 'together' ? null : 'together')}>
            {together ? `Samen · ${sync.members.length}` : 'Samen kijken'}
          </button>
          <button className="chip" aria-expanded={menu === 'quality'} onClick={() => setMenu(menu === 'quality' ? null : 'quality')}>
            {QUALITIES.find((q) => q.id === quality)?.label}
          </button>
          <button className="chip" aria-pressed={showInfo} onClick={() => setShowInfo((v) => !v)}>Info</button>
          <button className="icon-btn" onClick={toggleFullscreen} aria-label="Volledig scherm">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
          </button>
        </div>

        {menu && (
          <div className="player-menu" role="menu" data-focus-group>
            {menu === 'audio' && audioStreams.map((s) => (
              <button key={s.id} role="menuitemradio" aria-checked={!!s.selected} className="menu-item" onClick={() => void chooseAudio(s)}>
                {s.extendedDisplayTitle ?? s.displayTitle}
              </button>
            ))}
            {menu === 'subs' && (
              <>
                <button role="menuitemradio" aria-checked={!subStreams.some((s) => s.selected) && !extSub} className="menu-item" onClick={() => { setExtSub(null); if (subStreams.some((s) => s.selected)) void chooseSub(null); else setMenu(null); }}>Uit</button>
                {extSub && (
                  <button role="menuitemradio" aria-checked className="menu-item">
                    {langCode(extSub.lang)} · {extSub.release.slice(0, 50)}<span className="subtle"> · {extSub.source ?? 'OpenSubtitles'}</span>
                  </button>
                )}
                {extSub && (
                  <button role="menuitem" className="menu-item" disabled={syncing} onClick={() => void autoSync()}>
                    {syncing ? 'Synchroniseren…' : extFix ? `⟲ Opnieuw synchroniseren (nu ${extFix.offset > 0 ? '+' : ''}${extFix.offset.toFixed(1)} s)` : '⟲ Automatisch synchroniseren'}
                  </button>
                )}
                {subStreams.map((s) => (
                  <button key={s.id} role="menuitemradio" aria-checked={!!s.selected} className="menu-item" onClick={() => void chooseSub(s)}>
                    {s.extendedDisplayTitle ?? s.displayTitle}
                  </button>
                ))}
                {(trackUrl || extSub) && (
                  <div className="menu-delay" role="group" aria-label="Ondertitelvertraging">
                    <span className="subtle">Vertraging</span>
                    <button className="chip" onClick={() => setSubDelay((d) => Math.round((d - 0.5) * 10) / 10)}>−0,5 s</button>
                    <strong>{subDelay > 0 ? '+' : ''}{subDelay.toLocaleString('nl-NL')} s</strong>
                    <button className="chip" onClick={() => setSubDelay((d) => Math.round((d + 0.5) * 10) / 10)}>+0,5 s</button>
                    {subDelay !== 0 && <button className="chip" onClick={() => setSubDelay(0)}>Reset</button>}
                  </div>
                )}
                <button role="menuitem" className="menu-item" onClick={() => void searchSubs()}>🔍 Ondertitels zoeken…</button>
              </>
            )}
            {menu === 'subsearch' && (
              <>
                {subBusy && <div className="menu-item subtle" role="status">Downloaden: {subBusy}…</div>}
                {!subResults && !subBusy && <div className="menu-item subtle" role="status">Zoeken bij OpenSubtitles en je addons…</div>}
                {!subBusy && extResults && extResults.length > 0 && <div className="menu-item subtle">Via tumedia (speelt meteen, met synchroniseren)</div>}
                {!subBusy && extResults?.map((r) => (
                  <button key={`ext${r.fileId}`} role="menuitem" className="menu-item" onClick={() => void chooseExtSub(r)}>
                    {langCode(r.lang)} · {r.release.slice(0, 60)}
                    <span className="subtle"> · {r.source ?? 'OpenSubtitles'}{r.match ? ` · ${r.match}% passend` : ''}{r.hearingImpaired ? ' · SDH' : ''}</span>
                  </button>
                ))}
                {!subBusy && subResults && subResults.length > 0 && <div className="menu-item subtle">Via Plex (downloadt naar je server)</div>}
                {subResults?.length === 0 && extResults?.length === 0 && <div className="menu-item subtle">Niets gevonden. Staat "OpenSubtitles" aan bij de agents van je Plex-server?</div>}
                {!subBusy && subResults?.map((s, i) => (
                  <button key={`${(s as { key?: string }).key ?? i}`} role="menuitem" className="menu-item" onClick={() => void downloadSub(s)}>
                    {s.displayTitle ?? (s as { title?: string }).title ?? s.language}
                    <span className="subtle"> · {(s as { providerTitle?: string }).providerTitle ?? 'OpenSubtitles'}{s.score ? ` · score ${s.score}` : ''}</span>
                  </button>
                ))}
              </>
            )}
            {menu === 'volume' && ([
              ['off', 'Uit', 'Het originele geluid'],
              ['normal', 'Gelijkmatig volume', 'Harde stukken zachter, zachte dialogen verstaanbaar'],
              ['night', 'Nachtmodus', 'Sterk afgevlakt: knallen gaan niet meer door het huis'],
            ] as const).map(([id, label, hint]) => (
              <button key={id} role="menuitemradio" aria-checked={loudness === id} className="menu-item" onClick={() => chooseLoudness(id)}>
                {label} <span className="subtle">· {hint}</span>
              </button>
            ))}
            {menu === 'mark' && (
              <div className="menu-together">
                <span className="subtle">Help iedereen: markeer waar de intro, samenvatting of aftiteling begint en eindigt. Wordt ingestuurd bij IntroDB met jouw sleutel.</span>
                <div className="chip-wrap">
                  {([['intro', 'Intro'], ['recap', 'Samenvatting'], ['outro', 'Aftiteling']] as const).map(([id, label]) => (
                    <button key={id} className="chip" aria-pressed={mark.type === id} onClick={() => setMark({ type: id, start: null, end: null, msg: null })}>{label}</button>
                  ))}
                </div>
                <div className="chip-wrap">
                  <button className="chip" onClick={() => setMark((m) => ({ ...m, start: Math.round(time * 10) / 10, msg: null }))}>Begin = nu {mark.start !== null ? `(${formatClock(mark.start)})` : ''}</button>
                  <button className="chip" onClick={() => setMark((m) => ({ ...m, end: Math.round(time * 10) / 10, msg: null }))}>Einde = nu {mark.end !== null ? `(${formatClock(mark.end)})` : ''}</button>
                </div>
                <button className="chip" disabled={mark.start === null || mark.end === null || mark.end <= mark.start} onClick={() => void sendMark()}>Versturen</button>
                {mark.msg && <span className="subtle">{mark.msg}</span>}
              </div>
            )}
            {menu === 'together' && (
              together ? (
                <div className="menu-together">
                  <span className="subtle">Code voor de anderen</span>
                  <strong className="together-big">{together}</strong>
                  <span className="subtle">Of open op het andere apparaat: {location.origin}/together?code={together}</span>
                  <span>{sync.connected ? `Verbonden met: ${sync.members.map((m) => m.name).join(', ') || 'alleen jij'}` : 'Verbinden…'}</span>
                  <button className="chip" onClick={() => { setTogether(null); setMenu(null); const q = new URLSearchParams(location.search); q.delete('together'); history.replaceState(null, '', `${location.pathname}${q.toString() ? `?${q}` : ''}`); }}>Verlaten</button>
                </div>
              ) : (
                <div className="menu-together">
                  <span className="subtle">Kijk tegelijk met iemand op een ander apparaat: afspelen, pauzeren en spoelen gaat bij iedereen.</span>
                  <button className="chip" onClick={() => void startTogether()}>Kamer starten</button>
                </div>
              )
            )}
            {menu === 'quality' && QUALITIES.filter((q) => maxQuality === 'original' || (q.id !== 'original' && q.id <= maxQuality)).map((q) => (
              <button key={String(q.id)} role="menuitemradio" aria-checked={q.id === quality} className="menu-item" onClick={() => chooseQuality(q.id)}>
                {q.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Helpers ----------

function bufferedText(el: HTMLVideoElement): string {
  const b = el.buffered;
  if (!b.length) return 'leeg';
  const parts: string[] = [];
  for (let i = 0; i < b.length; i++) parts.push(`${b.start(i).toFixed(0)}-${b.end(i).toFixed(0)}s`);
  return parts.join(', ');
}

/** Kan deze browser het bestand zelf afspelen, zonder Plex ertussen? */
function canDirectPlay(media: PlexMedia, el: HTMLVideoElement): boolean {
  if ((media.Part?.length ?? 0) !== 1) return false;
  const container = (media.container ?? '').toLowerCase();
  if (!['mp4', 'm4v', 'mov'].includes(container)) return false;

  const v = (media.videoCodec ?? '').toLowerCase();
  const a = (media.audioCodec ?? '').toLowerCase();
  const safari = browserPlatform() === 'Safari';

  const videoOk =
    v === 'h264' ||
    (v === 'hevc' && (safari || el.canPlayType('video/mp4; codecs="hvc1.1.6.L120.90"') !== '')) ||
    (v === 'av1' && el.canPlayType('video/mp4; codecs="av01.0.08M.08"') !== '');
  const audioOk = ['aac', 'mp3'].includes(a) || (safari && ['ac3', 'eac3'].includes(a));

  return videoOk && audioOk;
}

async function findNextEpisode(plex: PlexClient, ep: PlexItem): Promise<PlexItem | null> {
  if (!ep.parentRatingKey) return null;
  const inSeason = await plex.children(ep.parentRatingKey);
  const idx = inSeason.findIndex((e) => e.ratingKey === ep.ratingKey);
  if (idx >= 0 && idx < inSeason.length - 1) return inSeason[idx + 1];

  if (!ep.grandparentRatingKey) return null;
  const seasons = await plex.children(ep.grandparentRatingKey);
  const sIdx = seasons.findIndex((s) => s.ratingKey === ep.parentRatingKey);
  const nextSeason = sIdx >= 0 ? seasons[sIdx + 1] : undefined;
  if (!nextSeason) return null;
  const eps = await plex.children(nextSeason.ratingKey);
  return eps[0] ?? null;
}

/** Pauzescherm: na een paar seconden pauze rustig in beeld waar je naar kijkt (zoals Netflix). */
function PauseInfo({ item, paused }: { item: PlexItem; paused: boolean }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    setShow(false);
    if (!paused) return;
    const t = window.setTimeout(() => setShow(true), 5000);
    return () => window.clearTimeout(t);
  }, [paused]);
  if (!show) return null;
  const isEp = item.type === 'episode';
  return (
    <div className="pause-info" aria-live="polite">
      <span className="pause-k">Je kijkt naar</span>
      <h2>{isEp ? item.grandparentTitle : item.title}</h2>
      {isEp && <p className="pause-ep">Seizoen {item.parentIndex} · Aflevering {item.index} · {item.title}</p>}
      <p className="pause-meta">{[item.year, item.contentRating, item.duration ? `${Math.round(item.duration / 60000)} min` : null].filter(Boolean).join(' · ')}</p>
      {item.summary && <p className="pause-sum">{item.summary}</p>}
      <span className="pause-hint">Gepauzeerd</span>
    </div>
  );
}

/** "4K · HEVC · EAC3 5.1" voor de leeftijdskaart. */
function techLine(item: PlexItem): string | null {
  const m = item.Media?.[0];
  if (!m) return null;
  const res = m.videoResolution ? (/^4k$/i.test(m.videoResolution) ? '4K' : /^\d+$/.test(m.videoResolution) ? `${m.videoResolution}p` : m.videoResolution.toUpperCase()) : null;
  const ch = m.audioChannels ? (m.audioChannels >= 8 ? '7.1' : m.audioChannels >= 6 ? '5.1' : m.audioChannels === 2 ? 'stereo' : `${m.audioChannels}ch`) : null;
  return [res, m.videoCodec?.toUpperCase(), [m.audioCodec?.toUpperCase(), ch].filter(Boolean).join(' ')].filter(Boolean).join(' · ') || null;
}

